import type { GuideInput } from "@ckgo2/shared/schemas";
import type { Guide } from "@ckgo2/shared/types";

/**
 * Převod editoru průvodce: detail z API → hodnoty formuláře. Čistá funkce bez Reactu
 * (`guideForm.test.ts`).
 *
 * Formulář nese jen třináct zobrazených polí. Tělo uložení je výstup resolveru `guideSchema`
 * nad hodnotami formuláře, a ten nese jen klíče, které formulář má (`.default` mají jen
 * zobrazené `isActive` a `sortOrder`). Skryté sloupce (`email`, `phone`, `whatsapp`, `bioSk`,
 * `homeLocationId`, `youtubeChannelUrl`, `featuredVideos`, starý `photo`) tělo nenese a uložení
 * (PUT = `guideUpdateSchema`, partial) je v DB nechá.
 *
 * Domovskou destinaci (`homeDestinationId`, čte ji harmonogram) formulář nenese: je to zrcadlo
 * první země z `countryIds`, které počítá server (`countryIds: []` ji vynuluje). Tělo proto
 * nese `countryIds` vždy jako pole, i prázdné (`optionalArray` mění na `undefined` jen
 * `null`/`undefined`), a nikdy `homeDestinationId`, aby netvrdilo něco jiného než výběr zemí.
 */

/**
 * Detail z `GET /api/admin/guides/:id`. Sdílený `Guide` nezná `homeDestinationId` a texty vede
 * jako `string | undefined`, ačkoli D1 vrací `null`; převod proto čte texty přes `?? ""`.
 */
export type GuideDetail = Guide & { homeDestinationId?: string | null };

export type GuideFormData = Pick<
  GuideInput,
  | "name"
  | "slug"
  | "photoMediaId"
  | "bio"
  | "livingIn"
  | "yearsExperience"
  | "specializations"
  | "countryIds"
  | "interview"
  | "tips"
  | "galleryImageIds"
  | "isActive"
  | "sortOrder"
>;

export const GUIDE_FORM_DEFAULTS: GuideFormData = {
  name: "",
  slug: "",
  photoMediaId: null,
  bio: "",
  livingIn: "",
  specializations: [],
  yearsExperience: 0,
  countryIds: [],
  interview: [],
  tips: null,
  galleryImageIds: [],
  isActive: true,
  sortOrder: 0,
};

/**
 * Země průvodce pro formulář: uložené `countryIds`, a když jsou prázdné, domovská destinace
 * (průvodce založený přes API jen s `homeDestinationId`). Stejné pravidlo jako doplnění
 * v migraci sloupce; první uložení editoru pak zemi zapíše do `countryIds`.
 */
export function countriesFromDetail(g: GuideDetail): string[] {
  if (g.countryIds && g.countryIds.length > 0) return g.countryIds;
  return g.homeDestinationId ? [g.homeDestinationId] : [];
}

export function guideFormFromDetail(g: GuideDetail): GuideFormData {
  return {
    name: g.name ?? "",
    slug: g.slug ?? "",
    photoMediaId: g.photoMediaId ?? null,
    bio: g.bio ?? "",
    livingIn: g.livingIn ?? "",
    yearsExperience: g.yearsExperience ?? null,
    specializations: g.specializations ?? [],
    countryIds: countriesFromDetail(g),
    interview: g.interview ?? [],
    tips: g.tips ?? null,
    galleryImageIds: g.galleryImageIds ?? [],
    isActive: g.isActive ?? true,
    sortOrder: g.sortOrder ?? 0,
  };
}
